import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import type {
  AISuggestion,
  ArchivedSuggestion,
  SuggestionArchiveAction,
} from '@/types/ai-suggestions';
import { AISuggestionsManager } from '@/lib/managers/AISuggestionsManager';

const ARCHIVE_STORAGE_KEY = 'ai_suggestions_archive';
const MAX_ARCHIVED_ITEMS = 50;

type SuggestionsPhase = 'landing' | 'background-loading' | 'results' | 'error';

interface AISuggestionsState {
  suggestions: AISuggestion[];
  archivedSuggestions: ArchivedSuggestion[];
  remainingRequests: number | null;
  phase: SuggestionsPhase;
  errorMessage: string | null;

  fetchSuggestions: () => Promise<void>;
  fetchRemainingRequests: () => Promise<void>;
  loadArchivedSuggestions: () => Promise<void>;
  setPhase: (phase: SuggestionsPhase) => void;
  resetToLanding: () => void;
  archiveSuggestion: (suggestion: AISuggestion, action: SuggestionArchiveAction) => void;
  removeSuggestion: (id: string) => void;
  clearArchive: () => void;
}

/**
 * Global suggestions store using Zustand.
 * Manages AI suggestions, category selection, rate limiting, and phase transitions.
 * Supports background generation — modal closes, bubble shows, results ready on tap.
 */
export const useAISuggestionsStore = create<AISuggestionsState>((set, get) => ({
  suggestions: [],
  archivedSuggestions: [],
  remainingRequests: null,
  phase: 'landing',
  errorMessage: null,

  fetchSuggestions: async () => {
    set({ phase: 'background-loading', errorMessage: null });
    try {
      const manager = AISuggestionsManager.getInstance();
      const { suggestions, remainingRequests } = await manager.fetchSuggestions();
      set({
        suggestions,
        remainingRequests,
        phase: 'results',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to generate suggestions';
      console.error('[useAISuggestionsStore] Failed to fetch suggestions:', error);
      set({
        phase: 'error',
        errorMessage: message,
      });
    }
  },

  fetchRemainingRequests: async () => {
    try {
      const manager = AISuggestionsManager.getInstance();
      const remaining = await manager.fetchRemainingRequests();
      set({ remainingRequests: remaining });
    } catch (error) {
      console.error('[useAISuggestionsStore] Failed to fetch remaining requests:', error);
    }
  },

  loadArchivedSuggestions: async () => {
    try {
      const stored = await AsyncStorage.getItem(ARCHIVE_STORAGE_KEY);
      if (stored) {
        const parsed: ArchivedSuggestion[] = JSON.parse(stored);
        set({ archivedSuggestions: parsed.slice(0, MAX_ARCHIVED_ITEMS) });
      }
    } catch (error) {
      console.error('[useAISuggestionsStore] Failed to load archived suggestions:', error);
    }
  },

  setPhase: (phase: SuggestionsPhase) => {
    set({ phase });
  },

  resetToLanding: () => {
    set({
      suggestions: [],
      phase: 'landing',
      errorMessage: null,
    });
  },

  archiveSuggestion: (suggestion: AISuggestion, action: SuggestionArchiveAction) => {
    const archived: ArchivedSuggestion = {
      suggestion,
      action,
      archivedAt: new Date().toISOString(),
    };

    const updated = [archived, ...get().archivedSuggestions].slice(0, MAX_ARCHIVED_ITEMS);

    set((state) => ({
      suggestions: state.suggestions.filter((s) => s.id !== suggestion.id),
      archivedSuggestions: updated,
    }));

    AsyncStorage.setItem(ARCHIVE_STORAGE_KEY, JSON.stringify(updated)).catch((err) =>
      console.error('[useAISuggestionsStore] Failed to persist archive:', err),
    );

    const manager = AISuggestionsManager.getInstance();
    manager.dismissSuggestion(suggestion.id);
  },

  removeSuggestion: (id: string) => {
    set((state) => ({
      suggestions: state.suggestions.filter((s) => s.id !== id),
    }));
  },

  clearArchive: () => {
    set({ archivedSuggestions: [] });
    AsyncStorage.removeItem(ARCHIVE_STORAGE_KEY).catch((err) =>
      console.error('[useAISuggestionsStore] Failed to clear archive storage:', err),
    );
  },
}));
